import { applyImagePreprocessing } from '../../components/changesHtmlUtils';
import { injectColgroups, renderContent, wrapTemplateFieldTags } from '../../doc-engine';
import type { TDocDiffRuntime, TContentHooks, TDocDiffOptions } from '../../doc-engine';
import type { TElement } from '../model/elements';
import { trace } from '../trace';

export type TStaticRenderContext = {
  runtime: TDocDiffRuntime;
  hooks: TContentHooks;
  options: TDocDiffOptions;
  documentId: string;
  /** Blob access token, for turning `data-blob-name` into a real image url. */
  token: string;
  /** Only a different document or version invalidates the cache - a part switch must not. */
  key: string;
  /** What the runtime resolves that the stored html does not carry, as `id -> what it draws`. */
  drawnFromRuntime: ReadonlyMap<string, string>;
};

// Module-level, not a store or a context: the swap reads this through `syncStaticNow`, outside React.
let context: TStaticRenderContext | null = null;
// Heading numbering is not in the saved html, so a rendering can go stale while its element string is unchanged.
const rendered = new Map<string, string>();
// An element object is stable while its content is, so identity answers the same question without hashing html.
let renderedByElement = new WeakMap<TElement, string>();
// A commit rewrites an element's html but never its id, so an element keeps its last rendering while the new one renders.
const lastGoodById = new Map<string, string>();
const inFlight = new Set<string>();
const listeners = new Set<() => void>();

const NOTIFY_BATCH_MS = 60;
let notifyQueued = false;

// A render carries the generation it started in, so one landing after a clear is dropped instead of refilling an emptied cache.
let generation = 0;

/** Above this many changed ids, dropping every rendering beats searching them all for each id. */
const SEARCH_WORTH_IT_UP_TO = 8;

// Elements resolve one by one; batch so a 2,000-element document does not re-render per element.
const notify = () => {
  if (notifyQueued) return;
  notifyQueued = true;
  setTimeout(() => {
    notifyQueued = false;
    listeners.forEach((listener) => listener());
  }, NOTIFY_BATCH_MS);
};

// Never cleared, only replaced - an element with no context falls back to its raw stored html.
export const setStaticRenderContext = (next: TStaticRenderContext) => {
  // Element strings are immutable, so rendered output survives a part switch - only a different document
  // or version invalidates it.
  if (context && next.key !== context.key) {
    rendered.clear();
    lastGoodById.clear();
    inFlight.clear();
    renderedByElement = new WeakMap();
    generation++;
  } else if (context) {
    evictWhatTheRuntimeRedraws(context.drawnFromRuntime, next.drawnFromRuntime);
  }
  context = next;
  notify();
};

/**
 * Drop the renderings that would come out differently now, and only those.
 *
 * Clearing everything instead is correct and far too slow: on a 2,000-element report it redraws the
 * whole static half for one renamed heading. An element is redrawn only if it names a changed id -
 * the heading or caption itself, and every reference that draws its number or its words.
 */
const evictWhatTheRuntimeRedraws = (before: ReadonlyMap<string, string>, after: ReadonlyMap<string, string>) => {
  const changed = new Set<string>();
  after.forEach((draws, id) => {
    if (before.get(id) !== draws) changed.add(id);
  });
  before.forEach((_, id) => {
    if (!after.has(id)) changed.add(id);
  });
  if (!changed.size) return;

  const started = performance.now();
  generation++;
  // Keyed by element object rather than by html, so it cannot be searched for the changed ids.
  renderedByElement = new WeakMap();
  /**
   * Past a handful of changed ids, searching every rendering for each of them costs more than
   * rendering them again. Inserting a heading renumbers every heading below it, so this is the
   * ordinary case rather than the extreme one, and there the whole cache is the cheaper answer.
   */
  if (changed.size > SEARCH_WORTH_IT_UP_TO) {
    const dropped = rendered.size;
    rendered.clear();
    lastGoodById.clear();
    trace('static:evict-all', { changed: changed.size, dropped, ms: Math.round(performance.now() - started) });
    return;
  }
  const ids = [...changed];
  let dropped = 0;
  for (const html of [...rendered.keys()]) {
    if (!ids.some((id) => html.includes(id))) continue;
    rendered.delete(html);
    dropped++;
  }
  ids.forEach((id) => lastGoodById.delete(id));
  trace('static:evict', {
    changed: changed.size,
    dropped,
    kept: rendered.size,
    ms: Math.round(performance.now() - started),
  });
};

// Both maps: an element outside the live part keeps its identity across a commit.
export const invalidateNumbering = () => {
  rendered.clear();
  renderedByElement = new WeakMap();
  lastGoodById.clear();
  inFlight.clear();
  generation++;
  // The commit has already told the model's listeners, so without this the static half redraws before the cache is emptied.
  notify();
};

export const hasStaticRenderContext = (): boolean => context !== null;

export const subscribeStaticRender = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const renderStats = {
  started: 0,
  finished: 0,
  failed: 0,
  fallbacks: 0,
  cached: 0,
  noContext: 0,
  byElement: 0,
  byHtml: 0,
  imagePrep: 0,
};

// The browser lays columns out from `<colgroup>` and ignores the `colwidth` attribute the parser stores on cells.
const withColumnWidths = (html: string): string =>
  html.includes('colwidth=') && html.includes('<table') ? injectColgroups(html) : html;

// Stored html carries the bare field tag; shown without the wrapper class, a block field jumps by its padding once the rendering lands.
const shownAsStored = (html: string): string => withColumnWidths(wrapTemplateFieldTags(html));

// `renderContent`'s `wrapImagesForAlign` hook strips every img attribute except src/width/data-align, so preprocess images first.
export const renderedElement = (element: TElement): string => {
  const known = renderedByElement.get(element);
  if (known !== undefined) {
    renderStats.cached++;
    renderStats.byElement++;
    return known;
  }
  const hit = rendered.get(element.html);
  if (hit !== undefined) {
    renderStats.cached++;
    renderStats.byHtml++;
    renderedByElement.set(element, hit);
    return hit;
  }
  if (!context) {
    renderStats.noContext++;
    return shownAsStored(element.html);
  }

  const startedPrep = performance.now();
  const fallback =
    (element.id && lastGoodById.get(element.id)) ||
    shownAsStored(applyImagePreprocessing(context.documentId, context.token, element.html));
  renderStats.imagePrep += performance.now() - startedPrep;
  if (inFlight.has(element.html)) {
    renderStats.fallbacks++;
    return fallback;
  }

  const { runtime, hooks, options, documentId, token } = context;
  const prepared = applyImagePreprocessing(documentId, token, element.html);
  inFlight.add(element.html);
  renderStats.started++;
  const startedIn = generation;
  renderContent(prepared, runtime, options, 'current', hooks)
    .then((out) => {
      renderStats.finished++;
      if (generation !== startedIn) return;
      rendered.set(element.html, out);
      renderedByElement.set(element, out);
      if (element.id) lastGoodById.set(element.id, out);
    })
    .catch(() => {
      renderStats.failed++;
      if (generation !== startedIn) return;
      rendered.set(element.html, shownAsStored(prepared)); // an element that cannot render still shows its stored form
    })
    .finally(() => {
      // Only our own entry: after a clear this html may already be in flight again in a newer generation.
      if (generation === startedIn) inFlight.delete(element.html);
      notify();
    });
  return fallback;
};

// `lastGoodById` is kept - it is what a rewritten element falls back to while its new html renders.
export const evictStaleElements = (liveHtml: Iterable<string>) => {
  const live = new Set(liveHtml);
  for (const key of rendered.keys()) {
    if (!live.has(key)) rendered.delete(key);
  }
};
